import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

import { useSession } from '@/lib/useSession';
import { Pill } from '@/components/Pill';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { formatDate } from '@/lib/dateUtils';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { hasFaireViewer, relativeTime, useFaireRuns, type FaireRun, type FaireRunStatus } from '@/lib/useFaireRuns';

type RangeKey = '7d' | '30d' | 'mtd' | 'all';

function statusPillClass(status: FaireRunStatus): string {
  if (status === 'complete') {
    return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  }
  if (status === 'partial') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  }
  if (status === 'failed') {
    return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  }
  return 'bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted';
}

function StatusPill({ status }: { status: FaireRunStatus }) {
  return <Pill tone={statusPillClass(status)}>{status}</Pill>;
}

function rangeFloorIso(range: RangeKey): string | null {
  const now = new Date();
  if (range === '7d') {
    const d = new Date(now);
    d.setUTCDate(d.getUTCDate() - 7);
    return d.toISOString();
  }
  if (range === '30d') {
    const d = new Date(now);
    d.setUTCDate(d.getUTCDate() - 30);
    return d.toISOString();
  }
  if (range === 'mtd') {
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
  }
  return null;
}

function truncate(s: string | null | undefined, max: number): string {
  if (!s) return '';
  if (s.length <= max) return s;
  return `${s.slice(0, max)}...`;
}

export function FaireReports() {
  const { user } = useSession();
  const isViewer = hasFaireViewer(user?.groups, user?.superAdmin);

  const [range, setRange] = useState<RangeKey>('7d');
  const [statusFilter, setStatusFilter] = useState<FaireRunStatus | 'all'>('all');
  const runsQ = useFaireRuns(100);

  const filteredRuns = useMemo<FaireRun[]>(() => {
    const floor = rangeFloorIso(range);
    return runsQ.runs.filter((r) => {
      if (floor && r.started_at < floor) return false;
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      return true;
    });
  }, [runsQ.runs, range, statusFilter]);

  if (!isViewer) {
    return (
      <div>
        <div className="mb-6">
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Faire</p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Recent runs</h1>
        </div>
        <div className="border border-amp-line rounded-lg bg-amp-white p-6 dark:border-amp-dark-line dark:bg-amp-dark-surface">
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            You don't have access to Faire-Sage. Contact IT to request the cockpit-faire-sage-viewer role.
          </p>
        </div>
      </div>
    );
  }

  const toolbar = (
    <div className="flex flex-wrap gap-3">
      <div>
        <label htmlFor="range" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
          Date range
        </label>
        <select
          id="range"
          value={range}
          onChange={(e) => setRange(e.target.value as RangeKey)}
          className="border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="mtd">This month</option>
          <option value="all">All</option>
        </select>
      </div>
      <div>
        <label htmlFor="status" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
          Status
        </label>
        <select
          id="status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as FaireRunStatus | 'all')}
          className="border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="all">All</option>
          <option value="complete">Complete</option>
          <option value="partial">Partial</option>
          <option value="failed">Failed</option>
          <option value="running">Running</option>
        </select>
      </div>
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start justify-between mb-4 shrink-0">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Faire</p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Recent runs</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Per-run summary. Per-order detail lives inside the S3 pipefile (v1.1).
          </p>
        </div>
        <Link
          to="/sales/faire/errors"
          className="text-xs text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink inline-flex items-center gap-1"
        >
          View errors <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>

      <DataGridShell
        total={runsQ.totalCount}
        loaded={filteredRuns.length}
        countNoun="runs"
        toolbar={toolbar}
      >
        <table className="w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <th className="text-left font-medium px-4 py-2">Date</th>
              <th className="text-left font-medium px-4 py-2">Run ID</th>
              <th className="text-left font-medium px-4 py-2">Trigger</th>
              <th className="text-right font-medium px-4 py-2">Pulled</th>
              <th className="text-right font-medium px-4 py-2">In pipefile</th>
              <th className="text-right font-medium px-4 py-2">Skipped</th>
              <th className="text-left font-medium px-4 py-2">Status</th>
              <th className="text-left font-medium px-4 py-2">Pipefile key</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {runsQ.isLoading ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Loading runs...
                </td>
              </tr>
            ) : runsQ.error ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-red-700 dark:text-red-300">
                  Could not load runs: {runsQ.error.message}
                </td>
              </tr>
            ) : filteredRuns.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  No runs in this range.
                </td>
              </tr>
            ) : (
              filteredRuns.map((run) => (
                <tr key={run.run_id} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                  <td className="px-4 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                    <div>{formatDate(run.started_at)}</div>
                    <div className="text-xs">{relativeTime(run.started_at)}</div>
                  </td>
                  <td className="px-4 py-2 font-mono text-amp-ink dark:text-amp-dark-ink" title={run.run_id}>
                    {truncate(run.run_id, 12)}
                  </td>
                  <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">{run.trigger_source}</td>
                  <td className="px-4 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                    {run.orders_pulled ?? 0}
                  </td>
                  <td className="px-4 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                    {run.orders_in_pipefile ?? 0}
                  </td>
                  <td className="px-4 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                    {run.orders_skipped ?? 0}
                  </td>
                  <td className="px-4 py-2">
                    <StatusPill status={run.status} />
                  </td>
                  <td
                    className="px-4 py-2 font-mono text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted"
                    title={run.s3_pipefile_key ?? ''}
                  >
                    {truncate(run.s3_pipefile_key, 36)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}
